import { Card, Button } from '@suscri/ui';
import {
  ArrowLeft,
  BarChart3,
  Coins,
  Sparkles,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { trpc } from '../../lib/trpc';

// Panel de métricas SaaS (propuesta de monetización §13).
// KPIs como stat-tiles; rankings como barras de una sola serie
// (un único tono #7C3AED validado sobre superficie clara; etiquetas en tinta).

const PLAN_ORDER = ['FREE', 'STARTER', 'PRO', 'BUSINESS'];

function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="text-3xl font-bold text-gray-900">{value}</div>
      <div className="mt-1 text-sm font-semibold text-gray-700">{label}</div>
      {hint && <div className="mt-0.5 text-xs text-gray-500">{hint}</div>}
    </div>
  );
}

function BarList({
  items,
}: {
  items: { label: string; value: number; detail?: string }[];
}) {
  const max = Math.max(...items.map(i => i.value), 1);
  return (
    <div className="space-y-3">
      {items.map(item => (
        <div key={item.label}>
          <div className="mb-1 flex items-baseline justify-between text-sm">
            <span className="font-medium text-gray-800">{item.label}</span>
            <span className="text-gray-600">
              {item.value.toLocaleString('es-ES')}
              {item.detail ? (
                <span className="text-gray-400"> · {item.detail}</span>
              ) : null}
            </span>
          </div>
          <div className="h-3 w-full rounded bg-gray-100">
            <div
              className="h-3 rounded"
              style={{
                width: `${(item.value / max) * 100}%`,
                backgroundColor: '#7C3AED',
                minWidth: item.value > 0 ? '4px' : 0,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MetricsPage() {
  const navigate = useNavigate();
  const [token] = useState<string | null>(() =>
    localStorage.getItem('adminToken')
  );

  const { data, isLoading, isError } = trpc.admin.metrics.useQuery(
    { token: token || '' },
    { enabled: Boolean(token) }
  );

  if (!token) {
    return (
      <div className="p-6">
        No autorizado. Inicia sesión como administrador.
      </div>
    );
  }

  const pct = (v: number) => `${(v * 100).toFixed(0)}%`;
  const eur = (v: number) => `${v.toFixed(2).replace('.', ',')} €`;

  return (
    <div className="app-shell min-h-screen">
      {/* Header */}
      <div className="border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-4 py-5">
            <Button
              variant="outline"
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Volver
            </Button>
            <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Métricas del negocio
              </h1>
              <p className="text-sm text-gray-600">
                Conversión, activación, créditos y MRR (teórico hasta activar
                pagos)
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {isLoading && <p className="text-gray-600">Cargando métricas…</p>}
        {isError && (
          <p className="text-red-600">No se pudieron cargar las métricas.</p>
        )}

        {data && (
          <>
            {/* KPIs de usuarios */}
            <section>
              <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Users className="h-5 w-5 text-violet-600" /> Usuarios
              </h2>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <StatTile
                  label="Usuarios totales"
                  value={String(data.users.total)}
                  hint={`+${data.users.new30d} en 30 días`}
                />
                <StatTile
                  label="Conversión Free→pago"
                  value={pct(data.users.conversionRate)}
                  hint={`${data.users.paid} de pago`}
                />
                <StatTile
                  label="Activación"
                  value={pct(data.users.activationRate)}
                  hint={`${data.users.activated} usaron acciones premium`}
                />
                <StatTile
                  label="Cancelaciones (30 d)"
                  value={String(data.revenue.canceled30d)}
                />
              </div>
            </section>

            {/* KPIs de ingresos */}
            <section>
              <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Sparkles className="h-5 w-5 text-violet-600" /> Ingresos
                (teóricos hasta la Fase 5 — Stripe)
              </h2>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <StatTile
                  label="MRR"
                  value={eur(data.revenue.mrr)}
                  hint="Ingreso recurrente mensual"
                />
                <StatTile
                  label="ARPU"
                  value={eur(data.revenue.arpu)}
                  hint="Ingreso medio por usuario"
                />
                <StatTile
                  label="Plazas fundador"
                  value={`${data.revenue.foundersUsed}/${data.revenue.founderLimit}`}
                  hint="Pro a 7,99 €"
                />
                <StatTile
                  label="Packs vendidos"
                  value={String(data.credits.packsSold)}
                  hint={`${data.credits.packCreditsSold.toLocaleString('es-ES')} créditos`}
                />
              </div>
            </section>

            {/* KPIs de créditos */}
            <section>
              <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Coins className="h-5 w-5 text-violet-600" /> Créditos
              </h2>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <StatTile
                  label="Créditos consumidos"
                  value={data.credits.consumedTotal.toLocaleString('es-ES')}
                />
                <StatTile
                  label="Acciones ejecutadas"
                  value={String(data.credits.consumeEvents)}
                />
                <StatTile
                  label="Consumo medio/usuario"
                  value={data.credits.avgPerUser.toFixed(1)}
                />
              </div>
            </section>

            {/* Rankings */}
            <div className="grid gap-6 lg:grid-cols-2">
              <Card className="rounded-xl border-0 bg-white p-6 shadow-lg">
                <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
                  <Users className="h-4 w-4 text-violet-600" /> Usuarios por
                  plan
                </h3>
                <BarList
                  items={PLAN_ORDER.map(code => ({
                    label: code,
                    value: data.users.byPlan[code] ?? 0,
                  }))}
                />
              </Card>

              <Card className="rounded-xl border-0 bg-white p-6 shadow-lg">
                <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
                  <BarChart3 className="h-4 w-4 text-violet-600" /> Acciones
                  premium más usadas
                </h3>
                {data.credits.topActions.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    Todavía no se han consumido acciones premium.
                  </p>
                ) : (
                  <BarList
                    items={data.credits.topActions.map(a => ({
                      label: a.name,
                      value: a.creditsConsumed,
                      detail: `${a.executions} ejecuciones`,
                    }))}
                  />
                )}
              </Card>
            </div>

            <p className="text-xs text-gray-400">
              Datos a {new Date(data.generatedAt).toLocaleString('es-ES')}. Las
              cifras de ingresos se basan en los planes activados; serán reales
              cuando se integre la pasarela de pago.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
